import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
   BooleanReportParameter,
   defaultFalseBooleanReportParameter,
} from '@ndt/report-output/shared/models/boolean-report-parameter';
import { ReportCallDispositions } from '@ndt/report-output/shared/models/call-disposition-report-parameter';
import {
   ExtensionReportSortBy,
   ExtensionSortByReportParameter,
} from '@ndt/report-output/shared/models/extension-sort-by-report-parameter';
import { defaultAllMultiEntitySelectParameter } from '@ndt/report-output/shared/models/multi-entity-selection-report-parameter';
import { TimeZoneReportParameter } from '@ndt/report-output/shared/models/time-zone-report-parameters';
import { createDateRangeReportParameter } from '@ndt/reports/shared/models/date-range-report-parameter-value';
import { defaultTimeZoneParameter } from '@ndt/reports/shared/models/time-zone-parameter-value';
import {
   convertFromFormValueList,
   ParameterFormValueUnion,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-form-value';
import {
   CallDispositionsToIncludeReportParameter,
   convertToDateRangeReportParameter,
   convertToForm,
   EntitiesToIncludeParameter,
   ReportParametersUnion,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-union.model';
import { ReportParameterConfig } from '@ndt/reports/shared/report-parameter-list/report-parameter.config';
import { ReportParameters } from 'generated/graphql-types';
import { MissedCallsReportParameters } from 'projects/report-output/src/lib/missed-calls/missed-calls';
import { LcsDateTimeAdapter } from 'src/app/common-components/date-time-picker/configuration/date-time-adapter';
import { throwIfNullOrUndefined } from 'src/app/common-components/utils/type-safety';
import {
   ReportParameterFormService,
   ReportParameterFormServiceBase,
} from '../../shared/report-parameter-list/services/report-parameter-form.service';

export class MissedCallsParametersFormService
   extends ReportParameterFormServiceBase
   implements ReportParameterFormService<MissedCallsReportParameters>
{
   config: ReportParameterConfig[];

   form: UntypedFormGroup;

   constructor(fb: UntypedFormBuilder, private dateAdapter: LcsDateTimeAdapter<Date>) {
      super(fb);
      const initialValue: ReportParametersUnion[] = [
         {
            accountId: 0,
            all: true,
            names: [],
            parameterType: ReportParameters.ExtensionsToInclude,
            sortOrder: 1,
            values: [],
         },
         {
            accountId: 0,
            all: false,
            names: [],
            parameterType: ReportParameters.ExtensionGroupsToInclude,
            sortOrder: 2,
            values: [],
         },
         {
            parameterType: ReportParameters.DateRange,
            ...createDateRangeReportParameter(),
            sortOrder: 3,
         },
         {
            parameterType: ReportParameters.CallDispositionsToInclude,
            sortOrder: 4,
            value: ReportCallDispositions.Both,
         },
         {
            parameterType: ReportParameters.ExtensionSortBy,
            sortOrder: 5,
            value: ExtensionReportSortBy.Number,
         },
         {
            parameterType: ReportParameters.ExcludeInternal,
            sortOrder: 6,
            value: false,
         },

         {
            parameterType: ReportParameters.SummaryOnly,
            sortOrder: 7,
            value: false,
         },
         {
            parameterType: ReportParameters.IncludeNotMissedCalls,
            sortOrder: 8,
            value: false,
         },
         {
            parameterType: ReportParameters.TimeZone,
            sortOrder: 9,
            value: '',
         },
      ];

      this.config = initialValue.map((ap) => {
         return this.getReportParameterConfig(ap.parameterType);
      });

      this.form = convertToForm(initialValue, fb);
   }

   patchSavedValues(values: ReportParametersUnion[], showAskToggle: boolean, showAskParameters: boolean): void {
      throw new Error('Method not implemented.');
   }

   resetConfig(): void {
      throw new Error('Method not implemented.');
   }

   // getLayoutConfigurations(values: ReportParametersUnion[]): {
   //    name: string | null;
   //    color: string | null;
   //    refreshTimeSeconds: number | null;
   // } {
   //    throw new Error('Method not implemented.');
   // }

   setValue(values: ParameterFormValueUnion[]): void {
      this.form.setValue(values, { emitEvent: false });
   }

   getValue(includeAskParameters?: boolean | undefined): MissedCallsReportParameters | null {
      const formValue = this.form.getRawValue();
      const values: ParameterFormValueUnion[] = formValue.values;
      const convertedValues = convertFromFormValueList(values);
      return this.convertValue(convertedValues);
   }

   convertValue(values: ReportParametersUnion[]): MissedCallsReportParameters | null {
      const extensions = values.find((v) => {
         return v.parameterType === ReportParameters.ExtensionsToInclude;
      }) as EntitiesToIncludeParameter | undefined;

      const extensionGroups = values.find((v) => {
         return v.parameterType === ReportParameters.ExtensionGroupsToInclude;
      }) as EntitiesToIncludeParameter | undefined;

      const excludeInternal = values.find((v) => {
         return v.parameterType === ReportParameters.ExcludeInternal;
      }) as BooleanReportParameter | undefined;

      const extensionSortBy = values.find((v) => {
         return v.parameterType === ReportParameters.ExtensionSortBy;
      }) as ExtensionSortByReportParameter | undefined;

      const callDispositionsToInclude = values.find((v) => {
         return v.parameterType === ReportParameters.CallDispositionsToInclude;
      }) as CallDispositionsToIncludeReportParameter | undefined;

      const includeNotMissedCalls = values.find((v) => {
         return v.parameterType === ReportParameters.IncludeNotMissedCalls;
      }) as BooleanReportParameter | undefined;

      const summaryOnly = values.find((v) => {
         return v.parameterType === ReportParameters.SummaryOnly;
      }) as BooleanReportParameter | undefined;

      const timeZone = values.find((v) => {
         return v.parameterType === ReportParameters.TimeZone;
      }) as TimeZoneReportParameter | undefined;

      const output: MissedCallsReportParameters = {
         callDispositionsToInclude: callDispositionsToInclude
            ? {
                 value: callDispositionsToInclude.value,
              }
            : {
                 value: ReportCallDispositions.Both,
              },
         dateRange: throwIfNullOrUndefined(convertToDateRangeReportParameter(values, this.dateAdapter)),
         excludeInternal: excludeInternal
            ? {
                 value: excludeInternal.value,
              }
            : defaultFalseBooleanReportParameter,
         extensions: extensions
            ? {
                 accountId: extensions.accountId,
                 all: !!extensions?.all,
                 values: extensions.values || [],
              }
            : defaultAllMultiEntitySelectParameter,
         extensionGroups: extensionGroups
            ? {
                 accountId: extensionGroups.accountId,
                 all: !!extensionGroups?.all,
                 values: extensionGroups.values || [],
              }
            : defaultAllMultiEntitySelectParameter,
         extensionSortBy: extensionSortBy
            ? {
                 value: extensionSortBy.value,
              }
            : {
                 value: ExtensionReportSortBy.Number,
              },
         includeNotMissedCalls: includeNotMissedCalls
            ? {
                 value: includeNotMissedCalls.value,
              }
            : {
                 value: false,
              },
         summaryOnly: summaryOnly
            ? {
                 value: summaryOnly.value,
              }
            : {
                 value: false,
              },
         timeZone: timeZone
            ? {
                 value: timeZone.value,
              }
            : defaultTimeZoneParameter,
      };

      return output;
   }

   isListComplete(values: ReportParametersUnion[], includeAskParameters = false): boolean {
      const validParameters = !includeAskParameters
         ? values.filter((v) => {
              return !false;
           })
         : values;

      const dateRange = validParameters.find((v) => {
         return v.parameterType === ReportParameters.DateRange;
      });

      return !!dateRange;
   }
}
